import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, interval, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { EstudianteExamen, PagoEstudiante } from '../components/control-examen/models/estudiante-examen.model';
import { EstudianteService } from '../services/estudiante.service';
import { ExamenService } from '../services/examen.service';
import { SharedService } from '../services/shared.service';
import { PreciosExamen } from '../components/control-examen/models/precios-config.model';
import { Graduacion } from '../models/graduacion.model';
import { Instructor } from '../models/instructor.model';

export interface ExamSession {
  id: string;
  nombre: string;
  horaInicio: Date;
  isActive: boolean;
  estudiantes: EstudianteExamen[];
  timestamp: Date;
}

@Injectable({
  providedIn: 'root'
})
export class ControlExamenService {
  // Current single session becomes multi-session
  private sessionesSubject = new BehaviorSubject<ExamSession[]>([]);
  private currentSessionIdSubject = new BehaviorSubject<string | null>(null);

  sesiones$ = this.sessionesSubject.asObservable();
  currentSessionId$ = this.currentSessionIdSubject.asObservable();

  // Derived observables from current session
  estudiantesExamen$ = combineLatest([
    this.sesiones$,
    this.currentSessionId$
  ]).pipe(
    map(([sesiones, currentId]) => {
      const current = sesiones.find(s => s.id === currentId);
      return current?.estudiantes || [];
    })
  );

  private estudiantesExamenSubject = new BehaviorSubject<EstudianteExamen[]>([]);
  private isExamenActivoSubject = new BehaviorSubject<boolean>(false);
  private preciosExamenSubject = new BehaviorSubject<PreciosExamen>(new PreciosExamen());
  private updateInterval: any;

  preciosExamen$ = this.preciosExamenSubject.asObservable();
  // estudiantesExamen$ = this.estudiantesExamenSubject.asObservable();
  isExamenActivo$ = this.isExamenActivoSubject.asObservable();

  constructor(
    private estudianteService: EstudianteService,
    private examenService: ExamenService,
    private sharedService: SharedService
  ) {}

  // Create new session
  crearNuevaSession(nombre: string, horaInicio: Date): void {
    const newSession: ExamSession = {
      id: `session-${Date.now()}`,
      nombre: nombre,
      horaInicio: horaInicio,
      isActive: true,
      estudiantes: [],
      timestamp: new Date()
    };

    const sessions = this.sessionesSubject.value;
    sessions.push(newSession);
    this.sessionesSubject.next([...sessions]);
    
    this.currentSessionIdSubject.next(newSession.id);
  }

  // Change active session
  cambiarSession(sessionId: string | null): void {
    if (!sessionId) return;
    
    const sessions = this.sessionesSubject.value;
    const session = sessions.find(s => s.id === sessionId);
    
    if (session) {
      this.currentSessionIdSubject.next(sessionId);
    }
  }

  // Get current session
  getCurrentSession(): ExamSession | null {
    const sessionId = this.currentSessionIdSubject.value;
    if (!sessionId) return null;
    
    const sessions = this.sessionesSubject.value;
    return sessions.find(s => s.id === sessionId) || null;
  }

  // Start exam session
  iniciarExamen(nombrePrimerExamen?: string): void {
    this.isExamenActivoSubject.next(true);
    this.startPeriodicUpdates();
    
    // Create first session if none exist
    const sessions = this.sessionesSubject.value;
    if (sessions.length === 0) {
      const hora = new Date();
      const horaStr = `${hora.getHours().toString().padStart(2, '0')}:${hora.getMinutes().toString().padStart(2, '0')}`;
      const nombre = nombrePrimerExamen || `Examen ${horaStr}h`;
      this.crearNuevaSession(nombre, hora);
    }
  }

  // End exam session
  finalizarExamen(): void {
    this.isExamenActivoSubject.next(false);
    this.stopPeriodicUpdates();
    // Optionally clear the list
    // this.estudiantesExamenSubject.next([]);
  }

  // Add student from reception
  agregarEstudianteRecepcion(estudiante: EstudianteExamen): void {
    console.log('Adding student:', estudiante.nroLibreta);
    
    const currentSession = this.getCurrentSession();
    if (!currentSession) {
      console.error('No active session to add student to');
      return;
    }

    console.log('Current session:', currentSession.id, 'Students before:', currentSession.estudiantes.length);

    const sessions = this.sessionesSubject.value;
    const sessionIndex = sessions.findIndex(s => s.id === currentSession.id);
    
    if (sessionIndex === -1) {
      console.error('Session not found in sessions array');
      return;
    }

    // Check if student already exists in current session (by nroLibreta)
    const existingIndex = currentSession.estudiantes.findIndex(e => e.nroLibreta === estudiante.nroLibreta);
    
    // Create a NEW estudiantes array (immutable update)
    let updatedEstudiantes: EstudianteExamen[];
    if (existingIndex >= 0) {
      // Update existing student - create new array with updated student
      updatedEstudiantes = [
        ...currentSession.estudiantes.slice(0, existingIndex),
        estudiante,
        ...currentSession.estudiantes.slice(existingIndex + 1)
      ];
      console.log('Updated existing student at index', existingIndex);
    } else {
      // Add new student - create new array with new student
      updatedEstudiantes = [...currentSession.estudiantes, estudiante];
      console.log('Added new student');
    }
    
    // Create a NEW session object with NEW estudiantes array
    const updatedSession: ExamSession = {
      ...currentSession,
      estudiantes: updatedEstudiantes
    };
    
    // Update the sessions array with new session
    const updatedSessions = [
      ...sessions.slice(0, sessionIndex),
      updatedSession,
      ...sessions.slice(sessionIndex + 1)
    ];
    
    console.log('Emitting updated sessions, students count:', updatedEstudiantes.length);
    this.sessionesSubject.next(updatedSessions);
    
    // Validate asynchronously and update with new object reference
    this.validateEstudiante(estudiante).then(() => {
      console.log('Validation complete, creating new object reference');
      
      // Get the current state
      const currentSessions = this.sessionesSubject.value;
      const currentSessionIdx = currentSessions.findIndex(s => s.id === currentSession.id);
      if (currentSessionIdx === -1) return;
      
      const targetSession = currentSessions[currentSessionIdx];
      const studentIdx = targetSession.estudiantes.findIndex(e => e.nroLibreta === estudiante.nroLibreta);
      if (studentIdx === -1) return;
      
      // Create NEW student object with updated validation properties
      const updatedStudent = Object.assign(
        Object.create(Object.getPrototypeOf(estudiante)), 
        estudiante
      );
      
      // Create NEW students array with the new student object
      const newEstudiantes = [
        ...targetSession.estudiantes.slice(0, studentIdx),
        updatedStudent,
        ...targetSession.estudiantes.slice(studentIdx + 1)
      ];
      
      // Create NEW session with new students array
      const newSession: ExamSession = {
        ...targetSession,
        estudiantes: newEstudiantes
      };
      
      // Create NEW sessions array
      const newSessions = [
        ...currentSessions.slice(0, currentSessionIdx),
        newSession,
        ...currentSessions.slice(currentSessionIdx + 1)
      ];
      
      console.log('Emitting sessions with validated student');
      this.sessionesSubject.next(newSessions);
    });
  }

  // Remove student from list
  eliminarEstudiante(nroLibreta: number): void {
    console.log('Removing student:', nroLibreta); // Debug
    
    const currentSession = this.getCurrentSession();
    if (!currentSession) {
      console.error('No active session to remove student from');
      return;
    }

    const sessions = this.sessionesSubject.value;
    const sessionIndex = sessions.findIndex(s => s.id === currentSession.id);
    
    if (sessionIndex === -1) {
      console.error('Session not found in sessions array');
      return;
    }

    // Create NEW estudiantes array without the removed student
    const updatedEstudiantes = currentSession.estudiantes.filter(e => e.nroLibreta !== nroLibreta);
    
    console.log('Students before:', currentSession.estudiantes.length, 'after:', updatedEstudiantes.length);
    
    // Create a NEW session object with NEW estudiantes array
    const updatedSession: ExamSession = {
      ...currentSession,
      estudiantes: updatedEstudiantes
    };
    
    // Update the sessions array with new session
    const updatedSessions = [
      ...sessions.slice(0, sessionIndex),
      updatedSession,
      ...sessions.slice(sessionIndex + 1)
    ];
    
    console.log('Emitting updated sessions after removal');
    this.sessionesSubject.next(updatedSessions);
  }

  // Validate single student
  private async validateEstudiante(estudiante: EstudianteExamen): Promise<void> {
    try {
      // Check if student exists in database
      const estudianteBD = await this.estudianteService.getEstudiante(estudiante.nroLibreta).toPromise();
      
      if (estudianteBD) {
        estudiante.existeEnBD = true;
        estudiante.datosBaseDatos = estudianteBD;
        
        // Check data integrity
        const datosCoinciden = this.checkDataIntegrity(estudiante, estudianteBD);
        
        // Check if student took the exam (has new exam record with the expected graduation)
        const tomoExamen = await this.checkExamTaken(estudiante);
        
        // Check for missing exams in progression
        const { examenesCompletos, examenesFaltantes } = await this.checkCompleteProgression(estudiante);
        
        // Generate warnings
        const advertencias = this.generateWarnings(estudiante, datosCoinciden, examenesCompletos);
        
        estudiante.updateControlStatus(
          false,
          true,
          tomoExamen,
          datosCoinciden,
          examenesCompletos,
          advertencias,
          examenesFaltantes
        );
      } else {
        // This shouldn't happen as a 404 should trigger the catch block
        estudiante.updateControlStatus(
          false,
          false,
          false,
          false,
          false,
          ['Error al validar estudiante']
        );
      }
    } catch (error: any) {
      console.error('Error validating student:', error);
      
      // Check if this is a 404 Not Found error
      if (error.status === 404) {
        if (estudiante.idGraduacionActual <= 1) {  // Changed from < 1 to <= 1
          // New student case (BLANCO or no graduation)
          estudiante.updateControlStatus(
            true,    // nuevoEstudiante = true
            false,   // existeEnBD = false
            false,   // tomoExamen = false 
            false,   // datosCoinciden = false
            true,    // examenesCompletos = true
            estudiante.idGraduacionActual === 1 ? ['Cinturón blanco - sin exámenes previos'] : ['Nuevo estudiante']
          );
        } else {
          // Student with graduation but not found in database
          estudiante.updateControlStatus(
            false,   // nuevoEstudiante = false
            false,   // existeEnBD = false
            false,   // tomoExamen = false
            false,   // datosCoinciden = false
            false,   // examenesCompletos = false
            ['Estudiante no existe en la base de datos']  // advertencias
          );
        }
      } else {
        // Other errors
        estudiante.updateControlStatus(
          false,
          false,
          false,
          false,
          false,
          ['Error al validar estudiante']
        );
      }
    }
  }

  // Check data integrity between reception and database
  private checkDataIntegrity(recepcion: EstudianteExamen, baseDatos: any): boolean {
    return recepcion.apellido.toLowerCase() === baseDatos.apellido.toLowerCase() &&
           recepcion.nombre.toLowerCase() === baseDatos.nombre.toLowerCase() &&
           recepcion.idInstructor === baseDatos.idInstructor;
  }

  // Check if student took the exam (has new exam with expected graduation)
  private async checkExamTaken(estudiante: EstudianteExamen): Promise<boolean> {
    try {
      const examenes = await this.examenService.getExamenesByEstudiante(estudiante.nroLibreta).toPromise();
      
      if (!examenes || examenes.length === 0) return false;
      
      // Get last 2 days (today and yesterday) to handle timezone differences
      const validDates: string[] = [];
      for (let i = 0; i < 2; i++) {
        const date = new Date();
        date.setDate(date.getDate() - i); // Today, then yesterday
        
        const dateString = date.getFullYear() + '-' + 
                          String(date.getMonth() + 1).padStart(2, '0') + '-' + 
                          String(date.getDate()).padStart(2, '0');
        validDates.push(dateString);
      }
      
      // Check if there's an exam with the expected graduation in the last 2 days
      const recentExam = examenes.find(e => 
        validDates.includes(e.fcExamen) && e.idGraduacion === estudiante.idGraduacionExamen
      );
      
      return !!recentExam;
    } catch (error) {
      console.error('Error checking exam taken:', error);
      return false;
    }
  }

  // Check for complete belt progression
  private async checkCompleteProgression(estudiante: EstudianteExamen): Promise<{examenesCompletos: boolean, examenesFaltantes: string[]}> {
    try {
      // If student is BLANCO (id: 1), they don't have missing exams
      if (estudiante.idGraduacionActual <= 1) {
        return { examenesCompletos: true, examenesFaltantes: [] };
      }

      const examenes = await this.examenService.getExamenesByEstudiante(estudiante.nroLibreta).toPromise();
      const graduaciones = await this.sharedService.fetchGraduaciones().toPromise();
      
      if (!examenes || !graduaciones) {
        return { examenesCompletos: false, examenesFaltantes: [] };
      }

      // Get graduations the student should have (from 2 to current actual graduation)
      // Skip id=1 (BLANCO)
      const expectedGraduations = graduaciones.filter(g => 
        g.id > 1 && g.id <= estudiante.idGraduacionActual 
      ).sort((a, b) => a.id - b.id);

      // Get graduations the student actually has
      const actualGraduations = examenes.map(e => e.idGraduacion).sort((a, b) => a - b);

      // Find missing graduations
      const missingGraduations = expectedGraduations.filter(expected => 
        !actualGraduations.includes(expected.id)
      );

      const examenesFaltantes = missingGraduations.map(g => g.descripcion);
      
      return {
        examenesCompletos: missingGraduations.length === 0,
        examenesFaltantes
      };
    } catch (error) {
      console.error('Error checking progression:', error);
      return { examenesCompletos: false, examenesFaltantes: [] };
    }
  }

  private generateWarnings(
    estudiante: EstudianteExamen, 
    datosCoinciden: boolean, 
    examenesCompletos: boolean
  ): string[] {
    const warnings: string[] = [];
    
    if (!datosCoinciden && estudiante.datosBaseDatos) {
      const discrepancies: string[] = [];
      
      // Check each field and specify which ones don't match
      if (estudiante.nombre.toLowerCase().trim() !== estudiante.datosBaseDatos.nombre?.toLowerCase().trim()) {
        discrepancies.push('nombre');
      }
      
      if (estudiante.apellido.toLowerCase().trim() !== estudiante.datosBaseDatos.apellido?.toLowerCase().trim()) {
        discrepancies.push('apellido');
      }
      
      if (estudiante.idInstructor !== estudiante.datosBaseDatos.idInstructor) {
        discrepancies.push('instructor');
      }
      
      if (discrepancies.length > 0) {
        warnings.push(`Datos no coinciden: ${discrepancies.join(', ')}`);
      } else {
        warnings.push('Los datos de recepción no coinciden con la base de datos');
      }
    }
    
    if (!examenesCompletos && estudiante.examenesFaltantes && estudiante.examenesFaltantes.length > 0) {
      // Don't duplicate the missing exams info, it's already shown separately
      warnings.push('Progresión de exámenes incompleta');
    }
    
    return warnings;
  }

  // Update all students (called periodically)
  private async updateAllStudents(): Promise<void> {
    const currentSession = this.getCurrentSession();
    if (!currentSession) {
      console.error('No active session to update students');
      return;
    }

    const sessions = this.sessionesSubject.value;
    const sessionIndex = sessions.findIndex(s => s.id === currentSession.id);
    if (sessionIndex === -1) {
      console.error('Current session not found in sessions array');
      return;
    }

    // Validate all students
    for (const estudiante of currentSession.estudiantes) {
      await this.validateEstudiante(estudiante);
    }
    
    // Create NEW student objects with validation updates
    const updatedEstudiantes = currentSession.estudiantes.map(estudiante => 
      Object.assign(Object.create(Object.getPrototypeOf(estudiante)), estudiante)
    );
    
    // Create NEW session with new students array
    const updatedSession: ExamSession = {
      ...currentSession,
      estudiantes: updatedEstudiantes
    };
    
    // Create NEW sessions array
    const updatedSessions = [
      ...sessions.slice(0, sessionIndex),
      updatedSession,
      ...sessions.slice(sessionIndex + 1)
    ];
    
    console.log('All students updated with validation');
    this.sessionesSubject.next(updatedSessions);
  }

  // Start periodic updates every 15 minutes
  private startPeriodicUpdates(): void {
    this.updateInterval = interval(15 * 60 * 1000).subscribe(() => {
      this.updateAllStudents();
    });
  }

  // Stop periodic updates
  private stopPeriodicUpdates(): void {
    if (this.updateInterval) {
      this.updateInterval.unsubscribe();
      this.updateInterval = null;
    }
  }

  setPreciosExamen(precios: PreciosExamen): void {
    this.preciosExamenSubject.next(precios);
  }

  exportData(): string {
    const data = {
      timestamp: new Date().toISOString(),
      precios: this.preciosExamenSubject.value,
      sesiones: this.sessionesSubject.value.map(session => ({
        id: session.id,
        nombre: session.nombre,
        horaInicio: session.horaInicio,
        timestamp: session.timestamp,
        estudiantes: session.estudiantes.map(estudiante => ({
          ...estudiante,
          observaciones: estudiante.observaciones || ''
        }))
      })),
      currentSessionId: this.currentSessionIdSubject.value
    };
    return JSON.stringify(data, null, 2);
  }

  importData(jsonData: string): boolean {
    try {
      const data = JSON.parse(jsonData);
      
      // Check if this is new format (with sessions) or old format (single list)
      if (data.sesiones && Array.isArray(data.sesiones)) {
        // New multi-session format
        const sessions: ExamSession[] = data.sesiones.map((sessionData: any) => {
          const estudiantes = sessionData.estudiantes?.map((e: any) => {
            const pago: PagoEstudiante = {
              incluyeExamen: e.pago?.incluyeExamen ?? true,
              incluyeLibreta: e.pago?.incluyeLibreta ?? false,
              maderasSeleccionadas: [],
              precioIndumentaria: e.pago?.precioIndumentaria ?? 0,
              indumentariaDescripcion: e.pago?.indumentariaDescripcion ?? '',
              descuentoPorcentaje: e.pago?.descuentoPorcentaje ?? 0,
              descuentoValor: e.pago?.descuentoValor ?? 0,
              descExamenPct: e.pago?.descExamenPct ?? 0,
              descLibretaPct: e.pago?.descLibretaPct ?? 0,
              descMaderasPct: e.pago?.descMaderasPct ?? 0,
              descIndumentariaPct: e.pago?.descIndumentariaPct ?? 0,
              valorExamen: e.pago?.valorExamen ?? 0,
              valorLibreta: e.pago?.valorLibreta ?? 0,
              valorMaderas: e.pago?.valorMaderas ?? 0,
              valorIndumentaria: e.pago?.valorIndumentaria ?? 0,
              medioDePago: e.pago?.medioDePago ?? 'Efectivo',
              total: e.pago?.total ?? 0,
              pagado: true
            };

            // Reconstruct maderas
            if (e.pago?.maderasSeleccionadas && Array.isArray(e.pago.maderasSeleccionadas)) {
              pago.maderasSeleccionadas = e.pago.maderasSeleccionadas.map((madera: any) => ({
                tipo: {
                  id: madera.tipo.id,
                  nombre: madera.tipo.nombre,
                  precio: madera.tipo.precio
                },
                cantidad: madera.cantidad
              }));
            }

            // Use the static factory method
            const estudiante = EstudianteExamen.fromReceptionData({
              nroLibreta: e.nroLibreta,
              apellido: e.apellido,
              nombre: e.nombre,
              idInstructor: e.idInstructor,
              idGraduacionActual: e.idGraduacionActual,
              idGraduacionExamen: e.idGraduacionExamen,
              pago: pago
            });

            // Set additional properties
            estudiante.horaRegistro = e.horaRegistro ? new Date(e.horaRegistro) : new Date();
            estudiante.observaciones = e.observaciones || '';

            return estudiante;
          }) || [];
          
          return {
            id: sessionData.id,
            nombre: sessionData.nombre,
            horaInicio: new Date(sessionData.horaInicio),
            isActive: true,
            estudiantes: estudiantes,
            timestamp: new Date(sessionData.timestamp)
          };
        });
        
        this.sessionesSubject.next(sessions);
        
        if (data.precios) {
          this.preciosExamenSubject.next(new PreciosExamen(data.precios));
        }
        
        if (data.currentSessionId && sessions.find(s => s.id === data.currentSessionId)) {
          this.currentSessionIdSubject.next(data.currentSessionId);
        } else if (sessions.length > 0) {
          this.currentSessionIdSubject.next(sessions[0].id);
        }
        
        return true;
      } else if (data.estudiantesExamen && Array.isArray(data.estudiantesExamen)) {
        // Old single-list format - convert to single session
        const precios = data.preciosExamen ? new PreciosExamen(data.preciosExamen) : new PreciosExamen();
        
        const estudiantes = data.estudiantesExamen.map((e: any) => {
          const pago: PagoEstudiante = {
            incluyeExamen: e.pago?.incluyeExamen ?? true,
            incluyeLibreta: e.pago?.incluyeLibreta ?? false,
            maderasSeleccionadas: [],
            precioIndumentaria: e.pago?.precioIndumentaria ?? 0,
            indumentariaDescripcion: e.pago?.indumentariaDescripcion ?? '',
            descuentoPorcentaje: e.pago?.descuentoPorcentaje ?? 0,
            descuentoValor: e.pago?.descuentoValor ?? 0,
            descExamenPct: e.pago?.descExamenPct ?? 0,
            descLibretaPct: e.pago?.descLibretaPct ?? 0,
            descMaderasPct: e.pago?.descMaderasPct ?? 0,
            descIndumentariaPct: e.pago?.descIndumentariaPct ?? 0,
            valorExamen: e.pago?.valorExamen ?? 0,
            valorLibreta: e.pago?.valorLibreta ?? 0,
            valorMaderas: e.pago?.valorMaderas ?? 0,
            valorIndumentaria: e.pago?.valorIndumentaria ?? 0,
            medioDePago: e.pago?.medioDePago ?? 'Efectivo',
            total: e.pago?.total ?? 0,
            pagado: true
          };

          // Reconstruct maderas
          if (e.pago?.maderasSeleccionadas && Array.isArray(e.pago.maderasSeleccionadas)) {
            pago.maderasSeleccionadas = e.pago.maderasSeleccionadas.map((madera: any) => ({
              tipo: {
                id: madera.tipo.id,
                nombre: madera.tipo.nombre,
                precio: madera.tipo.precio
              },
              cantidad: madera.cantidad
            }));
          }

          // Use the static factory method
          const estudiante = EstudianteExamen.fromReceptionData({
            nroLibreta: e.nroLibreta,
            apellido: e.apellido,
            nombre: e.nombre,
            idInstructor: e.idInstructor,
            idGraduacionActual: e.idGraduacionActual,
            idGraduacionExamen: e.idGraduacionExamen,
            pago: pago
          });

          // Set additional properties
          estudiante.horaRegistro = e.horaRegistro ? new Date(e.horaRegistro) : new Date();
          estudiante.observaciones = e.observaciones || '';

          return estudiante;
        });
        
        // Create single session from old data
        const session: ExamSession = {
          id: `imported-${Date.now()}`,
          nombre: 'Examen Importado',
          horaInicio: new Date(data.timestamp || Date.now()),
          isActive: true,
          estudiantes: estudiantes,
          timestamp: new Date(data.timestamp || Date.now())
        };
        
        this.sessionesSubject.next([session]);
        this.currentSessionIdSubject.next(session.id);
        this.preciosExamenSubject.next(precios);
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error importing data:', error);
      return false;
    }
  }

  tablaCSV(): string {
    const sesiones = this.sessionesSubject.value;
    if (sesiones.length === 0) {
      return 'No hay sesiones';
    }
    
    let instructores: Instructor[] = [];
    let graduaciones: Graduacion[] = [];
    this.sharedService.instructores$.subscribe(inst => instructores = inst || []);
    this.sharedService.graduaciones$.subscribe(grad => graduaciones = grad || []);

    const escapeCsv = (text: string) => `"${text?.replace(/"/g, '""') || ""}"`;

    const header = [
      "Sesión",
      "Nro. Libreta",
      "Apellido",
      "Nombre",
      "Instructor",
      "Graduación Actual",
      "Graduación Examen",
      "Hora Registro",
      "Estado",
      "Observaciones",
      "$ Examen",
      "$ Libreta",
      "Maderas",
      "$ Maderas",
      "Indumentaria Desc.",
      "$ Indumentaria",
      "Descuento %",
      "Descuento $",
      "Medio de Pago",
      "$ Total",
      "Pagado"
    ].join(",");

    interface RowData {
      sessionNombre: string;
      sessionIndex: number;
      e: EstudianteExamen;
      instructorNombre: string;
      instructorApellido: string;
      gradActual: string;
      gradExamen: string;
      horaRegistro: string;
      estado: string;
      maderas: string;
      valorExamen: number;
      valorLibreta: number;
      valorMaderas: number;
      valorIndumentaria: number;
      indumentariaDesc: string;
      descuentoPct: number;
      descuentoVal: number;
      medioPago: string;
      total: number;
      pagado: string;
    }

    const allData: RowData[] = [];

    for (let sessionIdx = 0; sessionIdx < sesiones.length; sessionIdx++) {
      const session = sesiones[sessionIdx];
      for (const e of session.estudiantes) {
        const inst = instructores.find(i => i.id === e.idInstructor);
        const instructorNombre = inst ? `${inst.nombre} ${inst.apellido}` : e.idInstructor.toString();
        const instructorApellido = inst?.apellido || "";
        const gradActual = e.idGraduacionActual <= 1 ? "BLANCO" : (graduaciones.find(g => g.id === e.idGraduacionActual)?.descripcion || e.idGraduacionActual.toString());
        const gradExamen = e.idGraduacionExamen <= 1 ? "BLANCO" : (graduaciones.find(g => g.id === e.idGraduacionExamen)?.descripcion || e.idGraduacionExamen.toString());
        const horaRegistro = e.horaRegistro ? `${e.horaRegistro.getHours().toString().padStart(2, '0')}:${e.horaRegistro.getMinutes().toString().padStart(2, '0')}` : "";
        
        let estado = "Pendiente";
        if (e.tomoExamen) estado = "Tomó Examen";
        else if (!e.existeEnBD) estado = "No existe en BD";
        else if (!e.datosCoinciden) estado = "Datos no coinciden";
        else if (!e.examenesCompletos) estado = "Exámenes incompletos";
        
        const maderas = e.pago?.maderasSeleccionadas ? e.pago.maderasSeleccionadas.map(m => `${m.tipo.nombre}x${m.cantidad}`).join("; ") : "";
        const valorExamen = e.pago?.valorExamen ?? (e.pago?.incluyeExamen ? this.preciosExamenSubject.value.examen : 0);
        const valorLibreta = e.pago?.valorLibreta ?? (e.pago?.incluyeLibreta ? this.preciosExamenSubject.value.libreta : 0);
        const valorMaderas = e.pago?.valorMaderas ?? (e.pago?.maderasSeleccionadas ? e.pago.maderasSeleccionadas.reduce((sum, m) => sum + (m.tipo.precio * m.cantidad), 0) : 0);
        const valorIndumentaria = e.pago?.valorIndumentaria ?? e.pago?.precioIndumentaria ?? 0;
        const indumentariaDesc = e.pago?.indumentariaDescripcion || "";
        const descuentoPct = e.pago?.descuentoPorcentaje ?? 0;
        const descuentoVal = e.pago?.descuentoValor ?? 0;
        const medioPago = e.pago?.medioDePago ?? "Efectivo";
        const total = e.pago?.total || 0;
        const pagado = e.pago?.pagado ? "Sí" : "No";

        allData.push({
          sessionNombre: session.nombre, sessionIndex: sessionIdx,
          e, instructorNombre, instructorApellido,
          gradActual, gradExamen, horaRegistro, estado, maderas,
          valorExamen, valorLibreta, valorMaderas, valorIndumentaria,
          indumentariaDesc, descuentoPct, descuentoVal, medioPago, total, pagado
        });
      }
    }

    allData.sort((a, b) => {
      if (a.sessionIndex !== b.sessionIndex) return a.sessionIndex - b.sessionIndex;
      const cmp = a.instructorApellido.localeCompare(b.instructorApellido);
      if (cmp !== 0) return cmp;
      return a.e.nroLibreta - b.e.nroLibreta;
    });

    const toRow = (d: RowData): string => {
      return [
        escapeCsv(d.sessionNombre),
        d.e.nroLibreta.toString(),
        escapeCsv(d.e.apellido),
        escapeCsv(d.e.nombre),
        escapeCsv(d.instructorNombre),
        escapeCsv(d.gradActual),
        escapeCsv(d.gradExamen),
        d.horaRegistro,
        escapeCsv(d.estado),
        escapeCsv(d.e.observaciones || ""),
        d.valorExamen,
        d.valorLibreta,
        escapeCsv(d.maderas),
        d.valorMaderas,
        escapeCsv(d.indumentariaDesc),
        d.valorIndumentaria,
        d.descuentoPct,
        d.descuentoVal,
        escapeCsv(d.medioPago),
        d.total,
        d.pagado
      ].join(",");
    };

    const makeTotalsRow = (label: string, students: RowData[]): string => {
      let tExam = 0, tLib = 0, tMad = 0, tInd = 0, tDesc = 0, tTotal = 0;
      let cExam = 0, cLib = 0, cMad = 0;

      for (const d of students) {
        if (d.e.pago?.incluyeExamen) { tExam += d.valorExamen; cExam++; }
        if (d.e.pago?.incluyeLibreta) { tLib += d.valorLibreta; cLib++; }
        if (d.e.pago?.maderasSeleccionadas?.length) { tMad += d.valorMaderas; cMad++; }
        tInd += d.valorIndumentaria;
        tDesc += d.descuentoVal;
        tTotal += d.total;
      }

      return [
        label,
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        `${students.length} estudiantes`,
        `${tExam} (${cExam})`,
        `${tLib} (${cLib})`,
        "",
        `${tMad} (${cMad})`,
        "",
        tInd,
        "",
        tDesc,
        "",
        tTotal,
        ""
      ].join(",");
    };

    const sessionGroups = new Map<number, Map<string, RowData[]>>();
    for (const d of allData) {
      if (!sessionGroups.has(d.sessionIndex)) sessionGroups.set(d.sessionIndex, new Map());
      const instructorMap = sessionGroups.get(d.sessionIndex)!;
      const instructorKey = d.e.idInstructor.toString();
      if (!instructorMap.has(instructorKey)) instructorMap.set(instructorKey, []);
      instructorMap.get(instructorKey)!.push(d);
    }

    const outputRows: string[] = [];
    const sortedSessionIndices = Array.from(sessionGroups.keys()).sort((a, b) => a - b);
    for (const sessionIdx of sortedSessionIndices) {
      const instructorMap = sessionGroups.get(sessionIdx)!;
      const sortedInstructorKeys = Array.from(instructorMap.keys()).sort((a, b) => {
        const instA = instructores.find(i => i.id === parseInt(a));
        const instB = instructores.find(i => i.id === parseInt(b));
        const apellidoA = instA?.apellido || "";
        const apellidoB = instB?.apellido || "";
        return apellidoA.localeCompare(apellidoB);
      });

      for (const instructorKey of sortedInstructorKeys) {
        const students = instructorMap.get(instructorKey)!;
        for (const d of students) {
          outputRows.push(toRow(d));
        }
        const inst = instructores.find(i => i.id === parseInt(instructorKey));
        const sessionName = students[0].sessionNombre;
        const label = inst ? `SUBTOTAL ${inst.nombre} ${inst.apellido} (${sessionName})` : `SUBTOTAL Instructor ${instructorKey} (${sessionName})`;
        outputRows.push(makeTotalsRow(label, students));
        outputRows.push("");
      }
    }

    outputRows.push(makeTotalsRow("TOTALES GENERALES", allData));

    return header + "\r\n" + outputRows.join("\r\n");
  }

  tablaCSVSession(): string {
    const currentSession = this.getCurrentSession();
    if (!currentSession) {
      return 'No hay sesión activa';
    }
    
    let instructores: Instructor[] = [];
    let graduaciones: Graduacion[] = [];
    this.sharedService.instructores$.subscribe(inst => instructores = inst || []);
    this.sharedService.graduaciones$.subscribe(grad => graduaciones = grad || []);

    const escapeCsv = (text: string) => `"${text?.replace(/"/g, '""') || ""}"`;

    const header = [
      "Nro. Libreta",
      "Apellido",
      "Nombre",
      "Instructor",
      "Graduación Actual",
      "Graduación Examen",
      "Hora Registro",
      "Estado",
      "Observaciones",
      "$ Examen",
      "$ Libreta",
      "Maderas",
      "$ Maderas",
      "Indumentaria Desc.",
      "$ Indumentaria",
      "Descuento %",
      "Descuento $",
      "Medio de Pago",
      "$ Total",
      "Pagado"
    ].join(",");

    interface SessionRowData {
      e: EstudianteExamen;
      instructorNombre: string;
      instructorApellido: string;
      gradActual: string;
      gradExamen: string;
      horaRegistro: string;
      estado: string;
      maderas: string;
      valorExamen: number;
      valorLibreta: number;
      valorMaderas: number;
      valorIndumentaria: number;
      indumentariaDesc: string;
      descuentoPct: number;
      descuentoVal: number;
      medioPago: string;
      total: number;
      pagado: string;
    }

    const allData: SessionRowData[] = [];

    for (const e of currentSession.estudiantes) {
      const inst = instructores.find(i => i.id === e.idInstructor);
      const instructorNombre = inst ? `${inst.nombre} ${inst.apellido}` : e.idInstructor.toString();
      const instructorApellido = inst?.apellido || "";
      const gradActual = e.idGraduacionActual <= 1 ? "BLANCO" : (graduaciones.find(g => g.id === e.idGraduacionActual)?.descripcion || e.idGraduacionActual.toString());
      const gradExamen = e.idGraduacionExamen <= 1 ? "BLANCO" : (graduaciones.find(g => g.id === e.idGraduacionExamen)?.descripcion || e.idGraduacionExamen.toString());
      const horaRegistro = e.horaRegistro ? `${e.horaRegistro.getHours().toString().padStart(2, '0')}:${e.horaRegistro.getMinutes().toString().padStart(2, '0')}` : "";
      
      let estado = "Pendiente";
      if (e.tomoExamen) estado = "Tomó Examen";
      else if (!e.existeEnBD) estado = "No existe en BD";
      else if (!e.datosCoinciden) estado = "Datos no coinciden";
      else if (!e.examenesCompletos) estado = "Exámenes incompletos";
      
      const maderas = e.pago?.maderasSeleccionadas ? e.pago.maderasSeleccionadas.map(m => `${m.tipo.nombre}x${m.cantidad}`).join("; ") : "";
      const valorExamen = e.pago?.valorExamen ?? (e.pago?.incluyeExamen ? this.preciosExamenSubject.value.examen : 0);
      const valorLibreta = e.pago?.valorLibreta ?? (e.pago?.incluyeLibreta ? this.preciosExamenSubject.value.libreta : 0);
      const valorMaderas = e.pago?.valorMaderas ?? (e.pago?.maderasSeleccionadas ? e.pago.maderasSeleccionadas.reduce((sum, m) => sum + (m.tipo.precio * m.cantidad), 0) : 0);
      const valorIndumentaria = e.pago?.valorIndumentaria ?? e.pago?.precioIndumentaria ?? 0;
      const indumentariaDesc = e.pago?.indumentariaDescripcion || "";
      const descuentoPct = e.pago?.descuentoPorcentaje ?? 0;
      const descuentoVal = e.pago?.descuentoValor ?? 0;
      const medioPago = e.pago?.medioDePago ?? "Efectivo";
      const total = e.pago?.total || 0;
      const pagado = e.pago?.pagado ? "Sí" : "No";

      allData.push({
        e, instructorNombre, instructorApellido,
        gradActual, gradExamen, horaRegistro, estado, maderas,
        valorExamen, valorLibreta, valorMaderas, valorIndumentaria,
        indumentariaDesc, descuentoPct, descuentoVal, medioPago, total, pagado
      });
    }

    allData.sort((a, b) => {
      const cmp = a.instructorApellido.localeCompare(b.instructorApellido);
      return cmp !== 0 ? cmp : a.e.nroLibreta - b.e.nroLibreta;
    });

    const toRow = (d: SessionRowData): string => {
      return [
        d.e.nroLibreta.toString(),
        escapeCsv(d.e.apellido),
        escapeCsv(d.e.nombre),
        escapeCsv(d.instructorNombre),
        escapeCsv(d.gradActual),
        escapeCsv(d.gradExamen),
        d.horaRegistro,
        escapeCsv(d.estado),
        escapeCsv(d.e.observaciones || ""),
        d.valorExamen,
        d.valorLibreta,
        escapeCsv(d.maderas),
        d.valorMaderas,
        escapeCsv(d.indumentariaDesc),
        d.valorIndumentaria,
        d.descuentoPct,
        d.descuentoVal,
        escapeCsv(d.medioPago),
        d.total,
        d.pagado
      ].join(",");
    };

    const makeTotalsRow = (label: string, students: SessionRowData[]): string => {
      let tExam = 0, tLib = 0, tMad = 0, tInd = 0, tDesc = 0, tTotal = 0;
      let cExam = 0, cLib = 0, cMad = 0;

      for (const d of students) {
        if (d.e.pago?.incluyeExamen) { tExam += d.valorExamen; cExam++; }
        if (d.e.pago?.incluyeLibreta) { tLib += d.valorLibreta; cLib++; }
        if (d.e.pago?.maderasSeleccionadas?.length) { tMad += d.valorMaderas; cMad++; }
        tInd += d.valorIndumentaria;
        tDesc += d.descuentoVal;
        tTotal += d.total;
      }

      return [
        label,
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        `${students.length} estudiantes`,
        `${tExam} (${cExam})`,
        `${tLib} (${cLib})`,
        "",
        `${tMad} (${cMad})`,
        "",
        tInd,
        "",
        tDesc,
        "",
        tTotal,
        ""
      ].join(",");
    };

    const groups = new Map<string, SessionRowData[]>();
    for (const d of allData) {
      const key = d.e.idInstructor.toString();
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(d);
    }

    const outputRows: string[] = [];
    for (const [key, students] of groups) {
      for (const d of students) {
        outputRows.push(toRow(d));
      }
      const inst = instructores.find(i => i.id === parseInt(key));
      const label = inst ? `SUBTOTAL ${inst.nombre} ${inst.apellido}` : `SUBTOTAL Instructor ${key}`;
      outputRows.push(makeTotalsRow(label, students));
      outputRows.push("");
    }

    outputRows.push(makeTotalsRow("TOTALES", allData));

    return header + "\r\n" + outputRows.join("\r\n");
  }

  // Manual update trigger
  actualizarTodos(): void {
    this.updateAllStudents();
  }
}